import React, {
  useState, useEffect, useRef, useCallback, useContext, useMemo,
} from 'react';
import {
  Box, Card, CardMedia, Typography, IconButton,
  Chip, CircularProgress,
} from '@mui/material';
import {
  ExploreOutlined, SquareFootOutlined, PersonOutlineOutlined,
  Favorite, Share, LocationOn, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CustomSearchBar from './../Lease Module/CustomSearch';
import AuctionBottomNav from './AuctionBottomNav';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from '../../Api/ApiUrls';
import CopyFailureAlert from '../Alertpage/CopyAlert/CopyFailureAlert';
import CopySuccessAlert from '../Alertpage/CopyAlert/CopySuccessAlert';
import WishlistNotRemovedAlert from '../Alertpage/CopyAlert/WishlistNotRemovedAlert';
import WishlistRemovedAlert from '../Alertpage/CopyAlert/WishlistRemovedAlert';
import buildingImage from './../../../Images/house.jpeg';
import ShareModal from '../Buy Module/ShareModal';
import LocationModal from '../Buy Module/LocationModal';

const PAGE_SIZE = 20;
const API_BASE = 'https://landnest.net:7000/api';

const formatPriceShort = (price) => {
  const num = Number(price) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2).replace(/\.00$/, '')} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, '')} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1).replace(/\.0$/, '')} K`;
  return `₹${num}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return '--';
  }
};

// ─── Skeleton ───────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <Box sx={{
    mb: 1.2, mx: 2, borderRadius: 3, overflow: 'hidden', boxShadow: 2,
    backgroundColor: '#fff',
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
    '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
  }}>
    <Box sx={{ display: 'flex', height: 140 }}>
      <Box sx={{ width: '40%', backgroundColor: '#ddd' }} />
      <Box sx={{ width: '60%', p: 1.5 }}>
        <Box sx={{ height: 14, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '65%' }} />
        <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '45%' }} />
        <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1.5, width: '55%' }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
        </Box>
      </Box>
    </Box>
  </Box>
);

const ChipRowSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 0.6, px: 0, pb: 1, overflowX: 'hidden' }}>
    {[80, 60, 100, 70, 90].map((w, i) => (
      <Box key={i} sx={{
        width: w, height: 24, borderRadius: '20px', flexShrink: 0,
        backgroundColor: '#d0d0d0',
        animation: 'skeletonPulse 1.5s ease-in-out infinite',
        '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
      }} />
    ))}
  </Box>
);

const ScrollSentinel = ({ onVisible, hasMore, loading }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) onVisible(); },
      { threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, hasMore, loading]);

  return (
    <Box ref={ref} sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {loading && <CircularProgress size={28} sx={{ color: '#8B7355' }} />}
      {!loading && !hasMore && (
        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
          ✓ All saved auction properties loaded
        </Typography>
      )}
    </Box>
  );
};

// ─── Parse ──────────────────────────────────────────────────────────────────
const parseAuctionProperty = (item) => {
  if (!item) return null;
  return {
    id: String(item.id ?? item.property_id ?? ''),
    cart_id: item.cart_id,
    saved_at: item.saved_at,
    title: item.propertyType || item.property_type || 'Property',
    bankName: item.bankName || item.bank_name || '',
    bankContact: item.bankContactDetails || item.bank_contact_details || '',
    location: item.fullLocation || item.location || item.city || item.locality || 'Not specified',
    price: Number(item.price) || 0,
    emdAmount: Number(item.emdAmount ?? item.emd_amount) || 0,
    area: Number(item.area) || 0,
    units: item.units || 'Sq.ft',
    possessionStatus: item.possessionStatus || item.possession_status || 'N/A',
    auctionEnd: item.auctionEnd || item.auction_end || '',
    lat: item.lat,
    lng: item.lng ?? item.long,
    date: item.created_at?.split?.('T')?.[0] || item.saved_at?.split?.('T')?.[0],
    images: [buildingImage], // extend if API returns images
    propertyData: item,
  };
};

const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const usePaginatedFetch = (getUrl, deps, enabled = true) => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const fetchingRef = useRef(false);
  const abortRef = useRef(null);

  const setFetching = (val) => {
    fetchingRef.current = val;
    setIsFetching(val);
  };

  useEffect(() => {
    if (!enabled) {
      setInitialLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setItems([]);
    setCursor(null);
    setHasMore(false);
    setInitialLoading(true);
    setFetching(true);

    fetch(getUrl(null), { signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        const arr = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.properties)
            ? json.properties
            : [];
        setItems(arr.map(parseAuctionProperty).filter(Boolean));
        setCursor(json.next_cursor ?? null);
        setHasMore(!!json.has_more || arr.length >= PAGE_SIZE);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error('fetch error:', err);
      })
      .finally(() => {
        setInitialLoading(false);
        setFetching(false);
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore || !cursor) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);

    fetch(getUrl(cursor), { signal: ctrl.signal })
      .then((r) => r.json())
      .then((json) => {
        const arr = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.properties)
            ? json.properties
            : [];
        setItems((prev) => [...prev, ...arr.map(parseAuctionProperty).filter(Boolean)]);
        setCursor(json.next_cursor ?? null);
        setHasMore(!!json.has_more);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error('loadMore error:', err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, cursor, ...deps]);

  const removeItem = useCallback((propertyId) => {
    setItems((prev) => prev.filter((p) => p.id !== String(propertyId)));
  }, []);

  return {
    items, hasMore, loading, initialLoading, isFetching, fetchingRef, loadMore, removeItem,
  };
};

const useSavedAuctionProperties = ({ userId, searchQuery, priceFilter, typeFilter, enabled }) => {
  const getUrl = useCallback(
    (cursorVal) => {
      const p = new URLSearchParams({ limit: PAGE_SIZE });
      if (cursorVal) p.set('cursor', cursorVal);
      if (searchQuery?.trim()) p.set('search', searchQuery.trim());
      if (priceFilter?.min != null) p.set('min_price', priceFilter.min);
      if (priceFilter?.max != null) p.set('max_price', priceFilter.max);
      if (typeFilter) p.set('type', typeFilter);
      // ⚠️ Adjust if your saved endpoint differs
      return `${API_BASE}/properties/jvjd/auction/saved/${userId}?${p}`;
    },
    [userId, searchQuery, priceFilter, typeFilter]
  );

  return usePaginatedFetch(
    getUrl,
    [userId, searchQuery, JSON.stringify(priceFilter), typeFilter],
    enabled && !!userId
  );
};

// ─── Main ───────────────────────────────────────────────────────────────────
const AuctionSaves = () => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [searchQuery, setSearchQuery] = useState('');
  const [priceFilter, setPriceFilter] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [typeOptions, setTypeOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState({});

  const [showShareModal, setShowShareModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentPropertyData, setCurrentPropertyData] = useState(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [showNotRemoved, setShowNotRemoved] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const debouncedSearch = useDebounce(searchQuery, 400);
  const isTyping = searchQuery !== debouncedSearch;

  const {
    items: properties,
    hasMore,
    loading: listLoading,
    initialLoading,
    isFetching,
    fetchingRef,
    loadMore,
    removeItem,
  } = useSavedAuctionProperties({
    userId,
    searchQuery: debouncedSearch,
    priceFilter,
    typeFilter: selectedType,
    enabled: !!userId,
  });

  const showSkeleton = isTyping || isFetching || fetchingRef?.current;

  useEffect(() => {
    let cancelled = false;
    setFiltersLoading(true);
    fetch(`${API_BASE}/properties/jvjd/auction/options/filters/`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setTypeOptions(Array.isArray(data?.typeOptions) ? data.typeOptions : []);
      })
      .catch(() => {
        if (!cancelled) setTypeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFiltersLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setCurrentImageIndex((prev) => {
      const next = { ...prev };
      properties.forEach((p) => {
        if (p?.id != null && !(p.id in next)) next[p.id] = 0;
      });
      return next;
    });
  }, [properties]);

  const handleRemove = useCallback(async (property) => {
    try {
      if (property.cart_id) {
        await axios.delete(`${BASE_URL}/user-cart/${property.cart_id}/`);
      }
      removeItem(property.id);
      setAlertMessage('Property removed from wishlist.');
      setShowRemoved(true);
    } catch {
      setAlertMessage('Failed to remove property from wishlist.');
      setShowNotRemoved(true);
    }
  }, [removeItem]);

  const handleShareClick = useCallback((e, property) => {
    e.stopPropagation();
    setCurrentPropertyData(property);
    setShowShareModal(true);
  }, []);

  const handleLocationClick = useCallback((e, property) => {
    e.stopPropagation();
    setCurrentPropertyData(property);
    setShowLocationModal(true);
  }, []);

  const openGoogleMaps = useCallback(() => {
    if (!currentPropertyData) return;
    const pid = currentPropertyData.id;
    setLocationLoading((prev) => ({ ...prev, [pid]: true }));

    const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      currentPropertyData.location || ''
    )}`;

    const open = (url) => {
      setLocationLoading((prev) => ({ ...prev, [pid]: false }));
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const dest =
            currentPropertyData.lat && currentPropertyData.lng
              ? `https://www.google.com/maps/dir/?api=1&origin=${coords.latitude},${coords.longitude}&destination=${currentPropertyData.lat},${currentPropertyData.lng}&travelmode=driving`
              : fallback;
          open(dest);
        },
        () => open(fallback),
        { timeout: 10000 }
      );
    } else {
      open(fallback);
    }
  }, [currentPropertyData]);

  const renderCard = useCallback((property) => {
    if (!property?.id) return null;
    const imgIdx = currentImageIndex[property.id] || 0;
    const images = property.images?.length ? property.images : [buildingImage];

    return (
      <Card
        key={property.id}
        sx={{
          mb: 1, mx: 2, borderRadius: 3, boxShadow: 2,
          display: 'flex', height: { xs: 140, sm: 170 },
          transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'scale(1.015)', boxShadow: 4 },
          cursor: 'pointer',
        }}
        onClick={(e) => {
          if (e.target.closest('button') || e.target.closest('svg')) return;
          navigate(`/Post_Bankcard_indetail/${property.id}`, {
            state: { propertyId: property.id, property: property.propertyData },
          });
        }}
      >
        <Box sx={{ position: 'relative', width: '40%', minWidth: '40%', height: '100%', overflow: 'hidden' }}>
          <CardMedia
            component="img"
            image={images[imgIdx]}
            alt="Property"
            onError={(e) => { e.target.src = buildingImage; }}
            sx={{
              width: '100%', height: '100%', objectFit: 'cover',
              borderTopLeftRadius: 12, borderBottomLeftRadius: 12,
            }}
          />
          {property.bankName && (
            <Box sx={{
              position: 'absolute', top: 7, left: 5,
              bgcolor: 'rgba(0,0,0,0.65)', color: '#fff',
              px: 0.7, py: 0.2, borderRadius: '3px',
              fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase',
            }}>
              {property.bankName}
            </Box>
          )}
        </Box>

        <Box sx={{
          width: '60%', display: 'flex', flexDirection: 'column', minWidth: 0,
          height: '100%', p: { xs: 0.45, sm: 0.6 }, boxSizing: 'border-box',
          justifyContent: 'space-between',
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0, mb: 0.1 }}>
              <Typography noWrap sx={{
                flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                color: '#2C1810', fontWeight: 700,
                fontSize: { xs: '0.74rem', sm: '0.75rem' }, lineHeight: 1.2, mr: 0.3,
              }}>
                {property.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, flexShrink: 0 }}>
                <IconButton
                  onClick={(e) => { e.stopPropagation(); handleRemove(property); }}
                  size="small"
                  sx={{
                    width: 25, height: 25, p: 0, bgcolor: '#FFFFFF',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    '&:hover': { bgcolor: '#F5EDE4' },
                  }}
                >
                  <Favorite sx={{ fontSize: 17, color: '#D92D20' }} />
                </IconButton>
                <IconButton
                  onClick={(e) => handleShareClick(e, property)}
                  size="small"
                  sx={{
                    width: 25, height: 25, p: 0, bgcolor: '#FFFFFF',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    '&:hover': { bgcolor: '#F5EDE4' },
                  }}
                >
                  <Share sx={{ fontSize: 14, color: '#8D6E63' }} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mb: 0.05, mt: 1, cursor: 'pointer' }}
              onClick={(e) => handleLocationClick(e, property)}
            >
              <IconButton size="small" sx={{ p: 0.5 }} disabled={locationLoading[property.id]}>
                {locationLoading[property.id]
                  ? <CircularProgress size={14} />
                  : <LocationOn sx={{ color: '#B66A18', fontSize: '16px' }} />}
              </IconButton>
              <Typography noWrap sx={{
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                color: '#5D4037', fontSize: { xs: '0.68rem', sm: '0.8rem' },
                lineHeight: 1.1, fontWeight: 500,
              }}>
                {property.location || 'Location not available'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 0.15, mt: 1 }}>
              <Typography noWrap sx={{
                color: '#c98a2c', fontWeight: 700,
                fontSize: { xs: '0.90rem', sm: '0.95rem' }, lineHeight: 1.15,
              }}>
                {formatPriceShort(property.price)}
              </Typography>
              <Typography noWrap sx={{
                color: '#121212', fontSize: { xs: '0.60rem', sm: '0.58rem' }, fontWeight: 500,
              }}>
                {formatDate(property.date || property.auctionEnd)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            display: 'flex', width: '100%', minWidth: 0, borderRadius: '6px',
            overflow: 'hidden', height: { xs: 35, sm: 32 },
            bgcolor: '#f1ce9943', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <Box sx={{
              flex: 1, minWidth: 0, py: 0.25, px: 0.2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2,
              borderRight: '1px solid #E8DDD0',
            }}>
              <ExploreOutlined sx={{ fontSize: 13, color: '#8D6E63' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#121212', fontSize: { xs: '0.4rem', sm: '0.46rem' }, lineHeight: 2, fontWeight: 500 }}>
                  Possession
                </Typography>
                <Typography noWrap sx={{ color: '#2C1810', fontWeight: 700, fontSize: { xs: '0.45rem', sm: '0.52rem' }, lineHeight: 1.15 }}>
                  {property.possessionStatus || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              flex: 1, minWidth: 0, py: 0.25, px: 0.2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2,
              borderRight: '1px solid #E8DDD0',
            }}>
              <SquareFootOutlined sx={{ fontSize: 13, color: '#B66A18' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#121212', fontSize: { xs: '0.4rem', sm: '0.46rem' }, lineHeight: 2, fontWeight: 500 }}>
                  Area
                </Typography>
                <Typography noWrap sx={{ color: '#2C1810', fontWeight: 700, fontSize: { xs: '0.45rem', sm: '0.52rem' }, lineHeight: 1.15 }}>
                  {property.area ? `${property.area.toLocaleString('en-IN')} ${property.units}` : 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              flex: 1, minWidth: 0, py: 0.25, px: 0.2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2,
            }}>
              <PersonOutlineOutlined sx={{ fontSize: 13, color: '#8D6E63' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#121212', fontSize: { xs: '0.4rem', sm: '0.46rem' }, lineHeight: 2, fontWeight: 500 }}>
                  EMD
                </Typography>
                <Typography noWrap sx={{ color: '#2C1810', fontWeight: 700, fontSize: { xs: '0.45rem', sm: '0.52rem' }, lineHeight: 1.15 }}>
                  {formatPriceShort(property.emdAmount)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }, [
    currentImageIndex, navigate, handleRemove, handleShareClick,
    handleLocationClick, locationLoading,
  ]);

  const renderPropertiesSection = useMemo(() => {
    if (initialLoading) {
      return <Box sx={{ mt: 1 }}>{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</Box>;
    }
    if (showSkeleton) {
      return (
        <Box>
          {isTyping && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1.5 }}>
              <CircularProgress size={16} sx={{ color: '#8B7355' }} />
              <Typography variant="caption" sx={{ color: '#888' }}>Searching...</Typography>
            </Box>
          )}
          <Box sx={{ mt: isTyping ? 0.5 : 1 }}>
            {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          </Box>
        </Box>
      );
    }
    if (properties.length === 0) {
      return (
        <Box sx={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          alignItems: 'center', height: 200, gap: 1, px: 2,
        }}>
          <Typography variant="body2" color="text.secondary">
            {selectedType
              ? 'No saved auction properties for this type'
              : debouncedSearch
                ? `No results for "${debouncedSearch}"`
                : 'You have no saved auction properties yet.'}
          </Typography>
        </Box>
      );
    }
    return (
      <>
        {properties.map(renderCard)}
        <ScrollSentinel onVisible={loadMore} hasMore={hasMore} loading={listLoading} />
      </>
    );
  }, [
    initialLoading, showSkeleton, isTyping, properties, selectedType,
    debouncedSearch, renderCard, loadMore, hasMore, listLoading,
  ]);

  return (
    <Box sx={{ backgroundColor: 'rgb(239, 231, 221)', minHeight: '120vh' }}>
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
        backgroundColor: 'rgb(239, 231, 221)',
      }}>
        <Box sx={{ px: 1, pt: 1, pb: 0.25 }}>
          <CustomSearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPriceFilterChange={setPriceFilter}
          />
        </Box>
        <Box sx={{ px: 2, pt: 0, pb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Saved Auction Properties
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 0, pb: 0.5 }}>
          {filtersLoading ? (
            <ChipRowSkeleton />
          ) : (
            <Box sx={{
              display: 'flex', gap: 0.6, overflowX: 'auto', flexWrap: 'nowrap', pb: 0.5,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none', msOverflowStyle: 'none',
            }}>
              {(typeOptions || []).map((opt) => {
                if (!opt || opt.value == null) return null;
                const isActive = selectedType === opt.value;
                return (
                  <Chip
                    key={`type:${opt.value}`}
                    label={opt.label ?? String(opt.value)}
                    onClick={() => setSelectedType((prev) => (prev === opt.value ? null : opt.value))}
                    sx={{
                      flexShrink: 0, height: 24, borderRadius: '20px',
                      fontWeight: 'bold', fontSize: '13px',
                      bgcolor: isActive ? '#8B4F16' : 'transparent',
                      color: isActive ? '#fff' : '#000',
                      border: '1px solid black',
                      transition: 'all 0.2s ease', cursor: 'pointer',
                      '&:hover': { bgcolor: isActive ? '#333' : '#e0e0e0', transform: 'scale(1.02)' },
                      '& .MuiChip-label': { paddingLeft: '6px', paddingRight: '6px', lineHeight: 1 },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ pb: 8, pt: { xs: '105px', sm: '155px' } }}>
        {renderPropertiesSection}
      </Box>

      {showSuccess && (
        <CopySuccessAlert message={alertMessage} onClose={() => { setShowSuccess(false); setAlertMessage(''); }} />
      )}
      {showFailure && (
        <CopyFailureAlert message={alertMessage} onClose={() => { setShowFailure(false); setAlertMessage(''); }} />
      )}
      {showRemoved && (
        <WishlistRemovedAlert message={alertMessage} onClose={() => { setShowRemoved(false); setAlertMessage(''); }} />
      )}
      {showNotRemoved && (
        <WishlistNotRemovedAlert message={alertMessage} onClose={() => { setShowNotRemoved(false); setAlertMessage(''); }} />
      )}

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        ownerName={currentPropertyData?.bankName || 'Bank'}
        mobileNo={currentPropertyData?.bankContact}
      />
      <LocationModal
        open={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onOpenMap={openGoogleMaps}
        propertyLocation={currentPropertyData?.location}
      />

      <AuctionBottomNav />
    </Box>
  );
};

export default AuctionSaves;